<template>
  <b-container class="edit-questionnaire">
    <div class="float-right">
      <b-button variant="default" @click="cancel()">Cancel</b-button>
      <b-btn variant="info" @click.prevent="save">
        <template v-if="!saveLoading">Save</template>
        <template v-else>
          <b-spinner small></b-spinner>
          <span>Saving...</span>
        </template>
      </b-btn>
    </div>

    <h2>Create Questionnaire</h2>

    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Questionnaire Title:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
          :invalid-feedback="titleInvalidFeedback"
        >
          <b-form-input
            type="text"
            placeholder="Title"
            v-model="$v.title.$model"
            :state="validateState('title')"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Is Membership Type related:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
          :invalid-feedback="titleInvalidFeedback"
        >
          <b-checkbox switch v-model="isNonSociety" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="m-3" v-if="isNonSociety">
      <b-col>
        <b-form-group
          horizontal
          label="Membership Types:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
          :invalid-feedback="requiredInvalidFeedback"
          :state="validateState('nonSocietyMembershipTypes')"
        >
          <b-form-select
            v-model="$v.nonSocietyMembershipTypes.$model"
            multiple
            :options="membershipTypesOptions"
            :state="validateState('nonSocietyMembershipTypes')"
            :select-size="
              membershipTypesOptions.length < 7
                ? membershipTypesOptions.length
                : 7
            "
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="m-3" v-else>
      <b-col>
        <b-form-group
          horizontal
          label="Society:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
          :invalid-feedback="requiredInvalidFeedback"
        >
          <b-form-select
            v-model="$v.society_id.$model"
            :options="societyOptions"
            :state="validateState('society_id')"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Study Type:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
          :invalid-feedback="requiredInvalidFeedback"
        >
          <b-form-select
            v-model="$v.study_type_id.$model"
            :options="studyTypesOptions"
            :state="validateState('study_type_id')"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Api from '@/providers/Api.js';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import { validationMixin } from 'vuelidate';
import {
  required,
  minLength,
  maxLength,
  requiredIf,
  requiredUnless,
} from 'vuelidate/lib/validators';
import GetStudyTypeOptionsMixin from '@/mixins/GetStudyTypeOptionsMixin';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';

export default {
  name: 'questionnaireCreate',
  mixins: [
    validationMixin,
    GetStudyTypeOptionsMixin,
    GetMembershipTypeOptionsMixin,
  ],
  data() {
    return {
      isNonSociety: false,
      saveLoading: false,
      title: '',
      society_id: null,
      study_type_id: null,
      nonSocietyMembershipTypes: [],
    };
  },
  validations: {
    title: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(255),
    },
    society_id: {
      required: requiredUnless('isNonSociety'),
    },
    nonSocietyMembershipTypes: {
      required: requiredIf('isNonSociety'),
    },
    study_type_id: {
      required,
    },
  },
  computed: {
    societyOptions() {
      return this.$store.state.societyOptions;
    },
    requiredInvalidFeedback() {
      return 'This field is required';
    },
    titleInvalidFeedback() {
      if (!this.$v.title.maxLength) {
        return "Title can't be more than 255 characters.";
      }
      if (!this.$v.title.minLength) {
        return "Title can't be less than 3 characters.";
      }

      return 'This field is required';
    },
    studyTypesOptions: function () {
      const studyTypes = this.$store.state.studyTypes.items || [];
      return studyTypes.map((st) => {
        return { value: st.id, text: st.name };
      });
    },
    membershipTypesOptions: function () {
      return (this.$store.state.membershipTypes.items || []).map((m) => {
        return { value: m.id, text: m.type_name };
      });
    },
  },
  methods: {
    cancel() {
      this.$router.go(-1);
    },
    save() {
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      this.saveLoading = true;

      const data = {
        title: this.title,
        study_type_id: this.study_type_id,
        is_non_society: this.isNonSociety,
      };
      if (this.isNonSociety) {
        data.non_society_membership_types = this.nonSocietyMembershipTypes;
      } else {
        data.society_id = this.society_id;
      }

      Api.createQuestionnaire(data)
        .then((response) => {
          this.$toasted.success('Questionnaire saved', { duration: 3000 });
          this.$router.push(`/admin/questionnaire/edit/${response.data.id}`);
        })
        .catch((error) => {
          this.showErrors(error);
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },
    showErrors(error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss"></style>
